/**
 * Статус фоновой выплаты (msPayout) для экрана /result.
 *
 * Кабинет вызывает web-backend `GetPayoutStatus` (прокси к публичному
 * статусу владельца в mspayout). Контракт ответа (cabinet body):
 *   - status: "processing" | "succeeded" | "failed" (предпочтительно)
 *   - либо stage + stage_state ("failed" | "done" | "scheduled" | …)
 *     и is_terminal — признак конца pipeline от msPayout
 *   - error_text: безопасный пользовательский текст при failed
 *   - transaction: id транзакции, если уже создана
 *
 * Нельзя пробрасывать наружу сырой payout_events.error_info.
 */

export const PAYOUT_STATUS_PROCESSING = "processing";
export const PAYOUT_STATUS_SUCCEEDED = "succeeded";
export const PAYOUT_STATUS_FAILED = "failed";

const TERMINAL_STATUSES = new Set([
  PAYOUT_STATUS_PROCESSING,
  PAYOUT_STATUS_SUCCEEDED,
  PAYOUT_STATUS_FAILED,
]);

// Последняя стадия pipeline msPayout: только её `done` означает успех.
const FINAL_PAYOUT_STAGE = "finalize_transaction";

function asTrimmedString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";
  return String(value).trim();
}

/**
 * Флаг из JSON приходит и как boolean, и как "1"/"true" — приводим к boolean.
 */
function asBoolean(value) {
  if (typeof value === "boolean") return value;
  const text = asTrimmedString(value).toLowerCase();
  return text === "1" || text === "true";
}

/**
 * Берёт только безопасный пользовательский текст ошибки.
 * Объекты/массивы (технический payload) игнорируются.
 */
export function safePayoutErrorText(body) {
  if (!body || typeof body !== "object") return "";
  for (const key of ["error_text", "user_error_text", "error_message"]) {
    const text = asTrimmedString(body[key]);
    if (text) return text;
  }
  return "";
}

/**
 * Нормализует терминальный статус из ответа GetPayoutStatus.
 */
export function resolvePayoutTerminalStatus(body) {
  if (!body || typeof body !== "object") {
    return PAYOUT_STATUS_PROCESSING;
  }

  const explicit = asTrimmedString(
    body.status || body.payout_status || body.payout_terminal,
  ).toLowerCase();
  if (TERMINAL_STATUSES.has(explicit)) {
    // payout_status из CreatePayout может быть "confirmation_sent" и т.п. —
    // сюда попадает только явный processing/succeeded/failed.
    return explicit;
  }

  const stageState = asTrimmedString(
    body.stage_state || body.payout_stage_state,
  ).toLowerCase();
  if (stageState === "failed") {
    return PAYOUT_STATUS_FAILED;
  }
  if (stageState === "done") {
    // `done` есть у каждой промежуточной стадии (create_transaction,
    // send_gate, poll_gate), поэтому сам по себе он успехом не является:
    // терминальный успех — это is_terminal от msPayout либо
    // finalize_transaction:done. Иначе экран успеха показался бы до
    // отправки выплаты в шлюз.
    const stage = asTrimmedString(body.stage || body.payout_stage).toLowerCase();
    if (asBoolean(body.is_terminal) || stage === FINAL_PAYOUT_STAGE) {
      return PAYOUT_STATUS_SUCCEEDED;
    }
  }

  return PAYOUT_STATUS_PROCESSING;
}

/**
 * Решение по ответу GetPayoutStatus:
 *   - failed → показать ошибку, остановить polling
 *   - терминальный успех → перейти к GetParamsExtTransact
 *   - poll → продолжить опрос GetPayoutStatus
 */
export function getPayoutStatusDecision(body) {
  const status = resolvePayoutTerminalStatus(body);
  const errorText = safePayoutErrorText(body);
  const transaction = asTrimmedString(body?.transaction);

  if (status === PAYOUT_STATUS_FAILED) {
    return {
      action: "failed",
      shouldPoll: false,
      errorText,
      transaction,
      status,
    };
  }

  // Само появление transaction не завершает обработку выплаты: после
  // create_transaction ещё могут упасть send_gate, poll_gate или
  // finalize_transaction. До терминального успеха продолжаем опрос msPayout.
  if (status === PAYOUT_STATUS_SUCCEEDED) {
    return {
      action: "load_transaction",
      shouldPoll: false,
      errorText: "",
      transaction,
      status: PAYOUT_STATUS_SUCCEEDED,
    };
  }

  return {
    action: "poll",
    shouldPoll: true,
    errorText: "",
    transaction: "",
    status: PAYOUT_STATUS_PROCESSING,
  };
}

/**
 * Нужен ли опрос GetPayoutStatus для результата CreatePayout.
 */
export function shouldPollPayoutStatus(result) {
  if (!result || typeof result !== "object") return false;
  if (result.type !== "pay") return false;
  return Boolean(asTrimmedString(bodyPayoutCode(result)));
}

export function bodyPayoutCode(result) {
  if (!result || typeof result !== "object") return "";
  return asTrimmedString(result.payout_code || result.code);
}
